import { useEffect, useMemo, useState } from "react";
import { HauffeLoader } from "../components/BrandLogo";
import { Layout } from "../components/Layout";
import { Panel } from "../components/Panel";
import { getCurrentUser } from "../lib/api/authClient";
import {
  fetchRuntimeDepartmentWeighting,
  saveRuntimeDepartmentWeighting,
  type RuntimeDepartmentWeightingDetail
} from "../lib/api/runtimeWorkspaceClient";
import { useWorkspaceLanguage, withLanguagePrefix } from "../lib/i18n";
import { resolveRuntimeClusterForLanguage } from "../lib/i18n/languageResolvers";
import { getPerspectiveDisplayName, validateWeightDistribution } from "../lib/valueWorkflow";

interface DepartmentWeightingProps {
  departmentId: string;
  audience?: "contributor" | "owner";
}

export function DepartmentWeighting({ departmentId, audience = "contributor" }: DepartmentWeightingProps) {
  const language = useWorkspaceLanguage();
  const copy = language === "en" ? {
    eyebrow: "Criteria weighting",
    title: "Weight Customer Value Criteria",
    description: "Distribute 100% across the confirmed Customer Value Criteria from your perspective.",
    loadingTitle: "Weighting is loading",
    loadingLabel: "Customer Value Criteria are loading",
    errorTitle: "Weighting could not be loaded",
    loadError: "Weighting could not be loaded.",
    saved: "Draft saved.",
    submitted: "Weighting submitted.",
    saveError: "Weighting could not be saved.",
    submitError: "Weighting could not be submitted.",
    introTitle: "Your perspective",
    introBody: "Your weighting helps calculate the suggested baseline for the final Customer Value Calculation. The Company Owner can review and adjust the final weighting afterwards.",
    assigned: "Assigned",
    weightingLabel: "Weighting",
    saveDraft: "Save draft",
    submitFinal: "Submit weighting",
    submittedTitle: "Weighting submitted",
    submittedBody: "You can still adjust this weighting until the Company Owner confirms the final Customer Value Calculation.",
    readOnlyTitle: "Weighting is read-only",
    readOnlyBody: "The final Customer Value Calculation has been confirmed. This weighting can no longer be changed.",
    notOpenTitle: "Weighting is not open yet",
    notOpenBody: "This step opens after the Company Owner has confirmed the shared Customer Value Criteria.",
    assignedOnlyBody: "This weighting can only be edited by the assigned person.",
    languageTitle: "Criteria language",
    languageBody: "These criteria are not complete in the current language yet. Please ask the Company Owner to repair or regenerate the criteria before weighting.",
    back: audience === "owner" ? "Back to Customer Value Calculation" : "Back to my overview"
  } : {
    eyebrow: "Kriteriengewichtung",
    title: "Customer-Value-Kriterien gewichten",
    description: "Verteilen Sie 100% auf die bestätigten Customer-Value-Kriterien aus Sicht Ihrer Perspektive.",
    loadingTitle: "Gewichtung wird geladen",
    loadingLabel: "Customer-Value-Kriterien werden geladen",
    errorTitle: "Gewichtung konnte nicht geladen werden",
    loadError: "Gewichtung konnte nicht geladen werden.",
    saved: "Zwischenstand gespeichert.",
    submitted: "Gewichtung eingereicht.",
    saveError: "Gewichtung konnte nicht gespeichert werden.",
    submitError: "Gewichtung konnte nicht eingereicht werden.",
    introTitle: "Ihre Perspektive",
    introBody: "Ihre Gewichtung hilft, den vorgeschlagenen Basiswert für die finale Customer Value Calculation zu berechnen. Der Company Owner kann die finale Gewichtung danach prüfen und anpassen.",
    assigned: "Verteilt",
    weightingLabel: "Gewichtung",
    saveDraft: "Zwischenspeichern",
    submitFinal: "Gewichtung einreichen",
    submittedTitle: "Gewichtung eingereicht",
    submittedBody: "Sie können diese Gewichtung bis zur finalen Bestätigung der Customer Value Calculation durch den Company Owner weiter anpassen.",
    readOnlyTitle: "Gewichtung ist schreibgeschützt",
    readOnlyBody: "Die finale Customer Value Calculation wurde bestätigt. Diese Gewichtung kann nicht mehr geändert werden.",
    notOpenTitle: "Gewichtung noch nicht geöffnet",
    notOpenBody: "Dieser Schritt öffnet, nachdem der Company Owner die gemeinsamen Customer-Value-Kriterien bestätigt hat.",
    assignedOnlyBody: "Diese Gewichtung kann nur von der zugeordneten Person bearbeitet werden.",
    languageTitle: "Sprache der Kriterien",
    languageBody: "Diese Kriterien sind in der aktuellen Sprache noch nicht vollständig. Bitte bitten Sie den Company Owner, die Kriterien zu reparieren oder neu zu erstellen.",
    back: audience === "owner" ? "Zurück zur Customer Value Calculation" : "Zurück zu meiner Übersicht"
  };
  const [detail, setDetail] = useState<RuntimeDepartmentWeightingDetail | undefined>();
  const [currentUserId, setCurrentUserId] = useState<string | undefined>();
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();

  async function load() {
    setIsLoading(true);
    setError(undefined);
    try {
      const [next, currentUser] = await Promise.all([
        fetchRuntimeDepartmentWeighting(departmentId, audience),
        getCurrentUser()
      ]);
      setDetail(next);
      setCurrentUserId(currentUser?.id);
      const initial = next.weighting?.weights ?? Object.fromEntries(next.clusters.map((cluster) => [cluster.id, cluster.suggestedWeight]));
      setWeights(initial);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : copy.loadError);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience, departmentId]);

  const displayClusters = useMemo(() => (detail?.clusters ?? []).map((cluster) => resolveRuntimeClusterForLanguage(cluster, language)), [detail?.clusters, language]);
  const languageMismatch = useMemo(() => (detail?.clusters ?? []).some((cluster) => !cluster.contentI18n?.[language]), [detail?.clusters, language]);
  const validation = useMemo(() => {
    const relevantWeights = Object.fromEntries((detail?.clusters ?? []).map((cluster) => [cluster.id, weights[cluster.id] ?? 0]));
    return validateWeightDistribution(relevantWeights, language);
  }, [detail?.clusters, language, weights]);
  const isSubmitted = detail?.weighting?.status === "submitted";
  const processStatus = detail?.process?.status;
  const isOpen = processStatus === "weighting_open" || processStatus === "calculation_ready_for_owner_review";
  const isFinalReadOnly = processStatus === "calculation_confirmed";
  const isAssignedParticipant = Boolean(detail?.department.participantUserId && currentUserId && detail.department.participantUserId === currentUserId);
  const canEditWeighting = Boolean(detail && isAssignedParticipant && isOpen && !languageMismatch && !isFinalReadOnly);

  async function save(submit: boolean) {
    setIsSaving(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await saveRuntimeDepartmentWeighting(departmentId, weights, submit);
      setNotice(submit ? copy.submitted : copy.saved);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : submit ? copy.submitError : copy.saveError);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Layout eyebrow={copy.eyebrow} title={copy.title} description={copy.description}>
      {isLoading ? (
        <Panel title={copy.loadingTitle}>
          <HauffeLoader label={copy.loadingLabel} />
        </Panel>
      ) : null}
      {error ? (
        <Panel title={copy.errorTitle} variant="accent">
          <p className="form-error" role="alert">{error}</p>
        </Panel>
      ) : null}
      {notice ? (
        <Panel title={language === "en" ? "Saved" : "Gespeichert"} variant="accent">
          <p>{notice}</p>
        </Panel>
      ) : null}
      {!isLoading && detail ? (
        <div className="stack">
          <Panel title={copy.introTitle}>
            <p>{copy.introBody}</p>
            <div className="data-list">
              <div>
                <span>{language === "en" ? "Workspace" : "Workspace"}</span>
                <strong>{detail.workspace.name}</strong>
              </div>
              <div>
                <span>{language === "en" ? "Perspective" : "Perspektive"}</span>
                <strong>{getPerspectiveDisplayName(detail.department, language)}</strong>
              </div>
            </div>
          </Panel>
          {!isOpen && !isFinalReadOnly ? (
            <Panel title={copy.notOpenTitle} variant="accent">
              <p>{copy.notOpenBody}</p>
            </Panel>
          ) : null}
          {isFinalReadOnly ? (
            <Panel title={copy.readOnlyTitle} variant="accent">
              <p>{copy.readOnlyBody}</p>
            </Panel>
          ) : null}
          {isOpen && !isAssignedParticipant ? (
            <Panel title={copy.readOnlyTitle} variant="accent">
              <p>{copy.assignedOnlyBody}</p>
            </Panel>
          ) : null}
          {isSubmitted ? (
            <Panel title={copy.submittedTitle} variant="accent">
              <p>{copy.submittedBody}</p>
            </Panel>
          ) : null}
          {languageMismatch ? (
            <Panel title={copy.languageTitle} variant="accent">
              <p>{copy.languageBody}</p>
            </Panel>
          ) : null}
          {!languageMismatch ? <Panel title={copy.title} variant="accent">
            <div className="department-weighting-form">
              <div className={validation.ok ? "department-weighting-total department-weighting-total--ok" : "department-weighting-total department-weighting-total--error"}>
                <span>{copy.assigned}</span>
                <strong>{validation.total}%</strong>
                <p>{validation.message}</p>
              </div>
              <div className="department-weighting-list">
              {displayClusters.map((cluster) => (
                <article className={`department-weighting-row ${canEditWeighting ? "" : "department-weighting-row--readonly"}`} key={cluster.id}>
                  <div className="department-weighting-copy">
                    <strong>{cluster.name}</strong>
                    <p>{cluster.description}</p>
                  </div>
                  <label className="department-weighting-input">
                    <span>{copy.weightingLabel}</span>
                    <div className="department-weighting-number">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      disabled={!canEditWeighting || isSaving}
                      value={weights[cluster.id] ?? 0}
                      onChange={(event) => setWeights((current) => ({ ...current, [cluster.id]: Number(event.target.value) }))}
                    />
                      <span>%</span>
                    </div>
                  </label>
                  <div className="weight-bar department-weighting-bar" aria-hidden="true">
                    <span style={{ width: `${Math.min(100, Math.max(0, weights[cluster.id] ?? 0))}%` }} />
                  </div>
                </article>
              ))}
              </div>
            </div>
            <div className="hero-actions">
              <button className="button button-secondary" type="button" disabled={!canEditWeighting || isSaving} onClick={() => void save(false)}>
                {copy.saveDraft}
              </button>
              <button className="button button-primary" type="button" disabled={!canEditWeighting || isSaving || !validation.ok} onClick={() => void save(true)}>
                {copy.submitFinal}
              </button>
            </div>
          </Panel> : null}
          <a className="button button-secondary" href={withLanguagePrefix(audience === "owner" ? "/workspace/customer-value" : "/contributor", language)}>
            {copy.back}
          </a>
        </div>
      ) : null}
    </Layout>
  );
}
